/**
 * Input masks: what a field shows while typing and the plain value it keeps.
 * Numbers keep API decimal text ("1234567.5") and show grouped thousands
 * ("1 234 567.5"); phones keep "+998901234567" and show "+998 90 123 45 67".
 */

// The same no-break space formatDecimal groups with, so a typed and a shown amount look alike.
const GROUP = '\u00a0'

/**
 * Plain decimal text from what was typed: spaces dropped, a comma read as the
 * point, one point, at most `decimals` fraction digits (none when 0), no
 * leading zeros. A lone "-" or a trailing "." survive so typing can go on.
 */
export function parseNumber(text: string, decimals?: number, allowNegative = false): string {
  const compact = text.replace(/[\s\u00a0\u202f]/g, '').replace(/,/g, '.')
  const negative = allowNegative && compact.startsWith('-')
  const digits = compact.replace(/[^\d.]/g, '')
  const point = digits.indexOf('.')
  let int = point < 0 ? digits : digits.slice(0, point)
  // A count keeps the whole part of "12.5"; digits after a point are never glued on.
  if (decimals === 0) return `${negative ? '-' : ''}${int.replace(/^0+(?=\d)/, '')}`
  let fraction = point < 0 ? '' : digits.slice(point + 1).replace(/\./g, '')
  if (decimals !== undefined) fraction = fraction.slice(0, decimals)
  int = int.replace(/^0+(?=\d)/, '')
  if (point >= 0 && int === '') int = '0'
  return `${negative ? '-' : ''}${int}${point >= 0 ? `.${fraction}` : ''}`
}

/** Plain decimal text with its integer part grouped by thousands; anything else is shown as it is. */
export function groupNumber(value: string): string {
  const match = /^(-?)(\d*)(\.?)(\d*)$/.exec(value)
  if (!match) return value
  const [, sign, int, point, fraction] = match
  return `${sign}${int.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP)}${point}${fraction}`
}

/** Phone digits as the backend stores them: "+" and up to 15 digits, empty when there are none. */
export function parsePhone(text: string): string {
  const digits = text.replace(/\D/g, '').slice(0, 15)
  if (!digits) return ''
  return `+${digits.startsWith('998') ? digits.slice(0, 12) : digits}`
}

/** "+998 90 123 45 67" for an Uzbekistan number; another country's digits after "+" as typed. */
export function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (!digits) return ''
  if (!digits.startsWith('998')) return `+${digits}`
  const subscriber = digits.slice(3, 12)
  const parts = [subscriber.slice(0, 2), subscriber.slice(2, 5), subscriber.slice(5, 7), subscriber.slice(7, 9)].filter(Boolean)
  return ['+998', ...parts].join(' ')
}

/**
 * Where the caret goes after re-formatting: right after the `count`-th
 * significant character, so grouping spaces never push it around.
 */
export function caretAfter(formatted: string, count: number, significant: (char: string) => boolean): number {
  if (count <= 0) return formatted.startsWith('+') ? 1 : 0
  let seen = 0
  for (let index = 0; index < formatted.length; index++) {
    if (significant(formatted[index]) && ++seen === count) return index + 1
  }
  return formatted.length
}
